import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  MaterialReactTable,
  useMaterialReactTable,
  createMRTColumnHelper,
} from 'material-react-table';
import {
  Box,
  Button,
  createTheme,
  ThemeProvider,
  useTheme,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import Modal from 'react-modal';
import { getReportVehicles } from '../../actions/apiGetInfo';
import { lookupDepartmentNameById } from '../../actions/lookups';
import { returnDaysHours, readableStampSimpleMonthYear } from '../../utils/dateFunctions';
import ReportVehiclesModal from './ReportVehiclesModal';
import GroupAveragesChart from './GroupAveragesChart';
import NestedHistoryTable from './NestedHistoryTable';
import {
  formatDaysHours,
  getTotalMinutes,
  getLastSixMonthsReportTypes,
  computeAverageTime,
  getDepartmentAverageForRange,
  getGroupAverageForRange,
  getDealershipVehicleCount,
} from './chartUtils';
import _ from 'lodash';

const TableDepartmentsMonthly = ({ data, dealership_report_hours }) => {
  const globalTheme = useTheme();
  const dispatch = useDispatch();
  const {
    report_vehicles,
    report_department_data,
    installation,
    report_dealerships,
    departments,
    related_installations,
  } = useSelector((state) => ({
    report_vehicles: state.settings_reports.report_vehicles,
    report_department_data: state.settings_reports.report_department_data,
    installation: state.settings_dealerships.installation,
    report_dealerships: state.settings_reports.report_dealerships,
    departments: state.settings_departments.original_departments,
    related_installations: state.settings_dealerships.related_installations,
  }));

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState({ report_id: 0, department_id: 0, dealership: '' });
  const [selectedRange, setSelectedRange] = useState(null);
  const [loadingVehicles, setLoadingVehicles] = useState(false);

  useEffect(() => {
    if (loadingVehicles && report_vehicles.length > 0) {
      setLoadingVehicles(false);
      setModalIsOpen(true);
    }
  }, [report_vehicles, loadingVehicles]);

  const openModal = (report_id, department_id) => {
    if (report_id > 0) {
      setLoadingVehicles(true);
      setSelectedReport({ report_id, department_id });
      dispatch(getReportVehicles(report_id)).catch((error) => {
        setLoadingVehicles(false);
      });
    }
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setLoadingVehicles(false);
  };

  const openGroupModal = (range, department_id, dealership) => {
    if (!range || !(['7', '14', '30', '90'].includes(range) || /^[0-9]{4}_[0-9]{2}$/.test(range))) {
      return;
    }
    setSelectedReport(prev => ({ ...prev, department_id, dealership }));
    setSelectedRange(range);
    setGroupModalOpen(true);
  };

  const closeGroupModal = () => {
    setGroupModalOpen(false);
    setSelectedRange(null);
  };

  const tableTheme = useMemo(() => createTheme({
    palette: {
      mode: 'light',
    },
  }), [globalTheme]);

  const columnHelper = createMRTColumnHelper();

  const columns = [
    columnHelper.accessor('dealership', {
      header: 'Dealership',
      size: 120,
    }),
    columnHelper.accessor('department', {
      header: 'Department',
      accessorFn: (row) => {
        const dept = departments.find(d => parseInt(d.id) === parseInt(row.department_id));
        return dept ? (dept.display_name || dept.name || 'Unknown') : 'Unknown';
      },
    }),
    columnHelper.accessor('report_month', {
      header: 'Report Month',
    }),
    columnHelper.accessor('average_time', {
      header: 'Avg Days',
      Cell: ({ row }) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          <span className={row.original.average_time > (Number(row.original.goal) / 24) ? 'text-danger' : 'text-success'}>
            {formatDaysHours(row.original.average_time) || 'N/A'}
          </span>
        </Box>
      ),
    }),
    columnHelper.accessor('number_of_vehicles', {
      header: 'Vehicle Count',
    }),
    columnHelper.accessor('goal', {
      header: 'Goal',
      Cell: ({ row }) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          <span className={row.original.average_time > (Number(row.original.goal) / 24) ? 'text-danger' : 'text-success'}>
            {Number(row.original.goal) > 0 ? returnDaysHours(Number(row.original.goal)) : ''}
          </span>
        </Box>
      ),
    }),
    columnHelper.accessor('details', {
      header: 'Details',
      Cell: ({ row }) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          <ReportVehiclesModal department_id={row.original.department_id} report_id={row.original.report_id} />
        </Box>
      ),
    }),
  ];

  const now = new Date();
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const day = now.getDate().toString().padStart(2, '0');
  const year = now.getFullYear();
  const filename = `monthly_departments_report_${month}_${day}_${year}`;

  const csvConfig = mkConfig({
    fieldSeparator: ',',
    decimalSeparator: '.',
    useKeysAsHeaders: true,
    filename,
  });

  const exportableColumnKeys = ['dealership', 'department', 'report_month', 'average_time', 'number_of_vehicles', 'goal'];

  const reportDealerships = useMemo(() => {
    return report_dealerships ? report_dealerships.split(',').filter(d => d.trim()) : Object.keys(data.yAxis || {});
  }, [report_dealerships, data]);

  const tableData = useMemo(() => {
    if (!data || !reportDealerships.length || !data.xAxis || !Array.isArray(data.xAxis)) {
      return [];
    }

    return reportDealerships.flatMap((dealership) => {
      if (!data.yAxis?.[dealership]) {
        return [];
      }
      return data.xAxis.map((report_month, index) => {
        const number_of_vehicles = data.xCount?.[dealership]?.[index] || 0;
        if (number_of_vehicles > 0) {
          const averageTime = data.yAxis?.[dealership]?.[index] || 0;
          return {
            dealership,
            department: report_month || '',
            report_month,
            average_time: averageTime,
            report_id: data.dataId?.[dealership]?.[index] || '',
            department_id: data.departmentId?.[dealership]?.[index] || '',
            goal: data.goal?.[dealership]?.[index] || '',
            number_of_vehicles,
            vehicles: [],
          };
        }
        return null;
      }).filter(item => item !== null);
    });
  }, [data, reportDealerships, dealership_report_hours]);

  const handleExportToCSV = () => {
    const rowData = tableData.map(item => exportableColumnKeys.reduce((acc, key) => {
      acc[key] = key === 'department' ? (departments.find(d => parseInt(d.id) === parseInt(item.department_id))?.display_name || departments.find(d => parseInt(d.id) === parseInt(item.department_id))?.name || 'Unknown') :
                key === 'average_time' ? formatDaysHours(item.average_time) :
                key === 'goal' && Number(item.goal) > 0 ? returnDaysHours(Number(item.goal)) :
                item[key] || '';
      return acc;
    }, {}));
    const csv = generateCsv(csvConfig)(rowData);
    download(csvConfig)(csv);
  };

  const handleExportToPDFwGraph = () => {
    const doc = new jsPDF();
    const tableColumn = ['Dealership', 'Departments', 'Report Month', 'Average Time', 'Number of Vehicles', 'Goal'];
    const tableRows = tableData.map(item => [
      item.dealership,
      departments.find(d => parseInt(d.id) === parseInt(item.department_id))?.display_name || departments.find(d => parseInt(d.id) === parseInt(item.department_id))?.name || 'Unknown',
      item.report_month,
      formatDaysHours(item.average_time),
      item.number_of_vehicles,
      Number(item.goal) > 0 ? returnDaysHours(Number(item.goal)) : '',
    ]);

    const summaryElement = document.getElementById('report-summary');
    const summaryText = summaryElement ? summaryElement.textContent : '';

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    const maxTextWidth = pageWidth - margin * 2;

    const wrappedText = doc.splitTextToSize(summaryText, maxTextWidth);

    doc.setFontSize(12);
    doc.text(wrappedText, margin, 10);

    const textHeight = wrappedText.length * 10;
    const startYAfterText = textHeight + 10;

    const element = document.getElementById('monthly-chart');

    if (element) {
      html2canvas(element).then(canvas => {
        const imgData = canvas.toDataURL('image/png');
        const imgProps = doc.getImageProperties(imgData);
        const pdfWidth = doc.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        doc.addImage(imgData, 'PNG', 0, startYAfterText, pdfWidth, pdfHeight);

        autoTable(doc, {
          head: [tableColumn],
          body: tableRows,
          startY: startYAfterText + pdfHeight + 10,
        });
        doc.save(filename);
      });
    } else {
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: startYAfterText + 10,
      });
      doc.save(filename);
    }
  };

  const handleExportToPDF = () => {
    const doc = new jsPDF();
    const tableColumn = ['Dealership', 'Departments', 'Report Month', 'Average Time', 'Number of Vehicles', 'Goal'];
    const tableRows = tableData.map(item => [
      item.dealership,
      departments.find(d => parseInt(d.id) === parseInt(item.department_id))?.display_name || departments.find(d => parseInt(d.id) === parseInt(item.department_id))?.name || 'Unknown',
      item.report_month,
      formatDaysHours(item.average_time),
      item.number_of_vehicles,
      Number(item.goal) > 0 ? returnDaysHours(Number(item.goal)) : '',
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 10,
    });
    doc.save(filename);
  };

  const table = useMaterialReactTable({
    columns,
    data: tableData,
    enableRowSelection: false,
    enableExpanding: true,
    enablePagination: false,
    positionToolbarAlertBanner: 'bottom',
    initialState: { showColumnFilters: false, expanded: false },
    renderDetailPanel: ({ row }) => {
      const isExpanded = row.getIsExpanded();

      const lastSixMonths = getLastSixMonthsReportTypes();
      const nestedData = isExpanded ? (report_department_data || [])
        .filter(item =>
          item && item.dealership === row.original.dealership && item.department_id === row.original.department_id &&
          lastSixMonths.includes(item.report_type?.trim().toLowerCase()) &&
          item.department_type === 'department'
        )
        .map(item => {
          const average_time = computeAverageTime(item, dealership_report_hours);
          return {
            dealership: item.dealership || '',
            department: departments.find(d => parseInt(d.id) === parseInt(item.department_id))?.display_name || departments.find(d => parseInt(d.id) === parseInt(item.department_id))?.name || 'Unknown',
            report_month: readableStampSimpleMonthYear(item.recon_end_date) || '',
            average_time,
            average_time_dhm: formatDaysHours(average_time) || 'N/A',
            average_time_minutes: getTotalMinutes(average_time) || 0,
            number_of_vehicles: Number(item.vehicle_count) || 0,
            goal: item.goal || '',
            report_id: item.id || '',
            department_id: item.department_id || '',
          };
        }) : [];

      const timeRanges = ['7', '14', '30', '90'];
      const metricsRef = React.useRef(null);

      const handleExportDetailToPDF = () => {
        const doc = new jsPDF();
        const now = new Date();
        const month = (now.getMonth() + 1).toString().padStart(2, '0');
        const day = now.getDate().toString().padStart(2, '0');
        const year = now.getFullYear();
        const departmentName = departments.find(d => parseInt(d.id) === parseInt(row.original.department_id))?.display_name || departments.find(d => parseInt(d.id) === parseInt(row.original.department_id))?.name || 'Unknown';
        const filename = `department_summary_${month}_${day}_${year}.pdf`;

        // Add title
        doc.setFontSize(16);
        doc.text(`${departmentName} Summary`, 10, 10);

        // Capture metrics boxes
        if (metricsRef.current) {
          html2canvas(metricsRef.current).then(canvas => {
            const imgData = canvas.toDataURL('image/png');
            const imgProps = doc.getImageProperties(imgData);
            const pdfWidth = doc.internal.pageSize.getWidth() - 20;
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
            doc.addImage(imgData, 'PNG', 10, 20, pdfWidth, pdfHeight);

            // Add nested table
            if (nestedData.length > 0) {
              const tableColumn = ['Dealership', 'Department', 'Month', 'Avg Time', 'Vehicle Count'];
              const tableRows = nestedData.map(item => [
                item.dealership,
                item.department,
                item.report_month,
                item.average_time_dhm,
                item.number_of_vehicles,
              ]);
              autoTable(doc, {
                head: [tableColumn],
                body: tableRows,
                startY: 20 + pdfHeight + 10,
                styles: { fontSize: 10 },
              });
            }

            doc.save(filename);
          }).catch(() => {});
        }
      };

      const metricsHeader = isExpanded ? (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: '8px' }}>
            <Button
              onClick={handleExportDetailToPDF}
              startIcon={<FileDownloadIcon />}
              variant="contained"
              color="primary"
              size="small"
            >
              Export to PDF
            </Button>
          </Box>
          <Box ref={metricsRef} sx={{ padding: '8px', backgroundColor: '#f5f5f5', marginBottom: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#007bff' }}>
              {`${departments.find(d => parseInt(d.id) === parseInt(row.original.department_id))?.display_name || departments.find(d => parseInt(d.id) === parseInt(row.original.department_id))?.name || 'Unknown'} Summary`}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: '8px', flexWrap: 'wrap' }}>
              {timeRanges.map((range, index) => {
                const deptAvg = getDepartmentAverageForRange(row.original.department_id, range, report_department_data, dealership_report_hours, null, row.original.dealership);
                const groupAverage = getGroupAverageForRange(range, report_department_data, dealership_report_hours, null, row.original.department_id);
                const vehicleCount = getDealershipVehicleCount(row.original.dealership, range, report_department_data, row.original.department_id);
                const reportId = report_department_data.find(item => item.dealership === row.original.dealership && item.department_id === row.original.department_id && item.report_type?.trim().toLowerCase() === `step-${range}`)?.id || 0;
                return (
                  <Box
                    key={range}
                    sx={{
                      flex: '1 1 22%',
                      minWidth: '200px',
                      padding: '12px',
                      border: '2px solid #ccc',
                      borderRadius: '8px',
                      backgroundColor: index % 2 === 0 ? '#f8f9fa' : '#e9ecef',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: '#dee2e6',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#333' }}>
                      {`${range}-Day Avg`}
                    </Typography>
                    <Typography sx={{ color: deptAvg ? '#28a745' : '#dc3545' }}>
                      {deptAvg ? formatDaysHours(deptAvg) : 'N/A'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.9rem', color: '#555', cursor: 'pointer' }}>
                      Vehicles: {vehicleCount || 0} <ReportVehiclesModal department_id={row.original.department_id} report_id={reportId} />
                    </Typography>
                    {related_installations && related_installations.length > 1 && (
                                          <div
                                            onClick={() => openGroupModal(range, row.original.department_id, row.original.dealership)}
                                            className="btn btn-large btn-success btn-block space-top-1 text-center"
                                          >
                                            Group Avg: {groupAverage ? formatDaysHours(groupAverage) : 'N/A'}
                                          </div>
                                        )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      ) : null;

      const nestedColumns = [
        columnHelper.accessor('dealership', { header: 'Dealership', size: 120 }),
        columnHelper.accessor('department', { header: 'Department' }),
        columnHelper.accessor('report_month', { header: 'Month', size: 100 }),
        columnHelper.accessor('average_time', {
          header: 'Avg Time',
          Cell: ({ row }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span className={row.original.average_time > (Number(row.original.goal) / 24) ? 'text-danger' : 'text-success'}>
                {row.original.average_time_dhm}
              </span>
            </Box>
          ),
          sortingFn: (rowA, rowB) => rowA.original.average_time_minutes - rowB.original.average_time_minutes,
        }),
        columnHelper.accessor('number_of_vehicles', { header: 'Vehicle Count' }),
        columnHelper.accessor('goal', {
          header: 'Goal',
          Cell: ({ row }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span className={row.original.average_time > (Number(row.original.goal) / 24) ? 'text-danger' : 'text-success'}>
                {Number(row.original.goal) > 0 ? returnDaysHours(Number(row.original.goal)) : ''}
              </span>
            </Box>
          ),
        }),
        columnHelper.accessor('details', {
          header: 'Details',
          Cell: ({ row }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <ReportVehiclesModal department_id={row.original.department_id} report_id={row.original.report_id} />
            </Box>
          ),
        }),
      ];

      if (!isExpanded) return <div />;

      return (
        <Box sx={{ backgroundColor: '#f5f5f5', padding: '16px' }}>
          {metricsHeader}
          <NestedHistoryTable columns={nestedColumns} data={nestedData} />
        </Box>
      );
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box sx={{ display: 'flex', gap: '16px', padding: '8px', flexWrap: 'wrap' }}>
        <Button onClick={handleExportToCSV} startIcon={<FileDownloadIcon />}>
          Export to CSV
        </Button>
        <Button onClick={handleExportToPDF} startIcon={<FileDownloadIcon />}>
          Export to PDF
        </Button>
        <Button onClick={handleExportToPDFwGraph} startIcon={<FileDownloadIcon />}>
          Export to PDF (Include Graph)
        </Button>
      </Box>
    ),
  });

  const total_vehicles = useMemo(() => {
    return tableData.reduce((sum, item) => sum + (item.number_of_vehicles > 0 ? item.number_of_vehicles : 0), 0);
  }, [tableData]);

  const total_times = useMemo(() => {
    return tableData.reduce((sum, item) => sum + (item.number_of_vehicles > 0 ? item.number_of_vehicles * (item.average_time || 0) : 0), 0);
  }, [tableData]);

  const total_average_times = useMemo(() => {
    const validItems = tableData.filter(item => item.number_of_vehicles > 0 && item.average_time);
    return validItems.length > 0 ? total_times / total_vehicles : 0;
  }, [total_times, total_vehicles]);

  const range = useMemo(() => {
    switch (dealership_report_hours) {
      case 'age-24_7': return '(24/7)';
      case 'age-24_6': return '(24/6)';
      case 'age-24_5': return '(24/5)';
      default: return '(24/7)';
    }
  }, [dealership_report_hours]);

  return (
    <ThemeProvider theme={tableTheme}>
      <MaterialReactTable table={table} />
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Report Vehicles"
        className="customModal"
        ariaHideApp={false}
        overlayClassName="overlay"
      >
        <div className="customCard">
          <div className="customHeader">
            <button
              type="button"
              className="btn btn-danger btn-sm float-end space-left-2"
              aria-label="Close"
              onClick={closeModal}
            >
              <span aria-hidden="true">×</span>
            </button>
            <Typography variant="h5">Vehicle Report</Typography>
          </div>
          <ReportVehiclesModal department_id={selectedReport.department_id} report_id={selectedReport.report_id} />
        </div>
      </Modal>
      <Modal
        isOpen={groupModalOpen}
        onRequestClose={closeGroupModal}
        contentLabel="Group Averages"
        className="customModal"
        ariaHideApp={false}
        overlayClassName="overlay"
      >
        <div className="customCard" style={{ width: '50%', margin: '0 auto' }}>
          <div className="customHeader" style={{ textAlign: 'center' }}>
            <button
              type="button"
              className="btn btn-danger btn-sm float-end space-left-2"
              aria-label="Close"
              onClick={closeGroupModal}
            >
              <span aria-hidden="true">×</span>
            </button>
            <Typography variant="h5">{selectedRange ? `${selectedRange}-day Group Averages` : 'Loading...'}</Typography>
          </div>
          <Box sx={{ padding: '16px', textAlign: 'center' }}>
            <hr style={{ margin: '8px 0', width: '100%', border: '1px solid #ccc' }} />
            {selectedRange && report_department_data ? (
              <GroupAveragesChart
                range={selectedRange}
                reportDepartmentData={report_department_data}
                dealershipReportHours={dealership_report_hours}
                department_id={selectedReport.department_id}
              />
            ) : (
              <Typography>Loading data...</Typography>
            )}
          </Box>
        </div>
      </Modal>
    </ThemeProvider>
  );
};

export default React.memo(TableDepartmentsMonthly);